import React, { useEffect, useRef } from 'react';

const EASE = 'cubic-bezier(0.5, 0.05, 0.2, 1)';

// Sample a quadratic Bézier path into keyframes -> ONE continuous fluid curved movement
function curveFrames(from, to, dip, s0, s1, r0, r1) {
  const ctrl = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 + dip * 2 };
  const frames = [];
  for (let k = 0; k <= 40; k++) {
    const t = k / 40;
    const x = (1 - t) ** 2 * from.x + 2 * (1 - t) * t * ctrl.x + t * t * to.x;
    const y = (1 - t) ** 2 * from.y + 2 * (1 - t) * t * ctrl.y + t * t * to.y;
    const f = { transform: `translate(${x}px, ${y}px) scale(${s0 + (s1 - s0) * t})` };
    if (r0 !== undefined) f.borderRadius = `${r0 + (r1 - r0) * t}px`;
    frames.push(f);
  }
  return frames;
}

// 2s profile-select transition: one fluid curved swoop (30% downward dip) to the screen center
// at +50% scale over a dark purple backdrop, then home mounts and the avatar lands EXACTLY on
// the header profile icon (top-right) at its normal size.
export function TvProfileTransition({ profile, fromRect, onCommit, onDone }) {
  const ref = useRef(null);
  const bdRef = useRef(null);

  useEffect(() => {
    const swallow = (e) => { e.preventDefault(); e.stopImmediatePropagation(); };
    window.addEventListener('keydown', swallow, true);
    const el = ref.current, bd = bdRef.current;
    const W = window.innerWidth, H = window.innerHeight;
    const w0 = fromRect.width;
    const s1 = 1.5;
    const to1 = { x: (W - w0 * s1) / 2 - fromRect.left, y: (H - w0 * s1) / 2 - fromRect.top };
    const d1 = Math.hypot(to1.x, to1.y);
    const dip1 = Math.min(0.3 * d1, (H - fromRect.top - (to1.y / 2) - w0 * 1.4 - 16) / 2);
    const r0 = 0.006 * W; // 0.6vw
    el.animate(curveFrames({ x: 0, y: 0 }, to1, dip1, 1, s1), { duration: 850, fill: 'forwards', easing: EASE });
    bd.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 650, fill: 'forwards', easing: 'ease-out' });

    const timers = [setTimeout(onCommit, 800)];
    let doneCalled = false;
    const finish = () => { if (!doneCalled) { doneCalled = true; onDone(); } };

    // Phase 2: wait until the home header icon really exists, then land exactly on it.
    // La fin est pilotée par l'animation elle-même (onfinish) — jamais par un minuteur fixe,
    // sinon l'overlay est démonté en plein vol quand la TV rame au montage de l'accueil.
    const launchReturn = () => {
      const target = document.querySelector('[data-testid="tv-settings-btn"]');
      const r = target ? target.getBoundingClientRect() : { left: W - 80, top: 24, width: 42 };
      const s2 = r.width / w0;
      const to2 = { x: r.left - fromRect.left, y: r.top - fromRect.top };
      const dur = 800;
      const a2 = el.animate(curveFrames(to1, to2, 0.12 * Math.hypot(to2.x - to1.x, to2.y - to1.y), s1, s2, r0, w0 / 2),
        { duration: dur, fill: 'forwards', easing: EASE });
      bd.animate([{ opacity: 1 }, { opacity: 0 }], { duration: dur, fill: 'forwards', easing: 'ease-in' });
      a2.onfinish = finish;
      timers.push(setTimeout(finish, dur + 2500)); // filet de sécurité si onfinish n'est pas émis (vieux webOS)
    };
    const start = performance.now();
    const poll = setInterval(() => {
      const elapsed = performance.now() - start;
      const ready = document.querySelector('[data-testid="tv-settings-btn"]');
      if (elapsed >= 1100 && (ready || elapsed >= 1800)) {
        clearInterval(poll);
        launchReturn();
      }
    }, 50);

    return () => {
      window.removeEventListener('keydown', swallow, true);
      clearInterval(poll);
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, pointerEvents: 'none' }} data-testid="tv-profile-transition">
      <div ref={bdRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: '#150a26', opacity: 0 }} />
      <div ref={ref} style={{ position: 'absolute', left: fromRect.left, top: fromRect.top,
        width: fromRect.width, height: fromRect.height, borderRadius: '0.6vw', transformOrigin: '0 0',
        overflow: 'hidden', background: profile.avatar_color || '#E9641A',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800,
        fontSize: fromRect.width * 0.4, boxShadow: '0 1vw 4vw rgba(0,0,0,0.6)' }}>
        {profile.avatar_url
          ? <img src={profile.avatar_url} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (profile.name || '?')[0].toUpperCase()}
      </div>
    </div>
  );
}
